import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { uuid } from '../lib/uuid'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { ArrowLeft, CheckCircle, MessageSquare, X, Clock } from 'lucide-react'

const QUICK_REPLIES = [
  "Hi! Thanks for reaching out. How can I help you today?",
  "Could you share a bit more detail so I can help better?",
  "Give me a moment to look into this for you!",
  "I've sorted that out for you! Anything else you need?",
  "This has been resolved from our end. You're all set! 💛",
]

function playAlert() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    const osc  = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.setValueAtTime(880, ctx.currentTime)
    gain.gain.setValueAtTime(0.15, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.45)
  } catch (_) {}
}

export default function SupportDashboard() {
  const { user } = useAuth()
  const navigate  = useNavigate()

  const [isAgent,          setIsAgent]          = useState(null)
  const [agentDisplayName, setAgentDisplayName] = useState('')
  const [tickets,          setTickets]          = useState([])
  const [selectedId,       setSelectedId]       = useState(null)
  const [messages,         setMessages]         = useState([])
  const [input,            setInput]            = useState('')
  const [activeTab,        setActiveTab]        = useState('open')
  const [showResolve,      setShowResolve]      = useState(false)
  const [resolveNote,      setResolveNote]      = useState('')
  const [sending,          setSending]          = useState(false)
  const [newIds,           setNewIds]           = useState(new Set())

  const bottomRef    = useRef(null)
  const inputRef     = useRef(null)
  const msgChannelRef = useRef(null) // broadcast channel for active ticket

  // ── Check agent access ───────────────────────────────────
  useEffect(() => {
    if (!user) return
    supabase.from('cs_agents')
      .select('id, name')
      .eq('email', user.email)
      .maybeSingle()
      .then(({ data }) => {
        setIsAgent(!!data)
        if (data?.name) setAgentDisplayName(data.name)
      })
  }, [user?.email])

  // ── Request browser notification permission ──────────────
  useEffect(() => {
    if (!isAgent) return
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [isAgent])

  // ── Load tickets + realtime for new ticket alerts ────────
  useEffect(() => {
    if (!isAgent) return

    supabase.from('support_tickets')
      .select('*')
      .order('updated_at', { ascending: false })
      .then(({ data }) => setTickets(data || []))

    const ch = supabase.channel('cs-tickets-all')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_tickets' },
        ({ eventType, new: n }) => {
          if (eventType === 'INSERT') {
            setTickets(p => [n, ...p])
            setNewIds(s => new Set([...s, n.id]))
            setTimeout(() => setNewIds(s => { const ns = new Set(s); ns.delete(n.id); return ns }), 12000)
            // Desktop notification + sound
            playAlert()
            if (Notification.permission === 'granted') {
              new Notification('New support request 💬', {
                body: `${n.couple_name || 'A customer'} needs help on Okoiya`,
                icon: '/okoiya-logo.png',
              })
            }
          } else if (eventType === 'UPDATE') {
            setTickets(p => p.map(t => t.id === n.id ? n : t))
          }
        })
      .subscribe()

    return () => supabase.removeChannel(ch)
  }, [isAgent])

  // ── Load messages + Broadcast subscription for active ticket ─
  useEffect(() => {
    if (!selectedId) {
      setMessages([])
      if (msgChannelRef.current) {
        supabase.removeChannel(msgChannelRef.current)
        msgChannelRef.current = null
      }
      return
    }

    setNewIds(s => { const ns = new Set(s); ns.delete(selectedId); return ns })

    // Load history from DB
    supabase.from('support_messages')
      .select('*')
      .eq('ticket_id', selectedId)
      .order('created_at', { ascending: true })
      .then(({ data }) => setMessages(data || []))

    // Subscribe to Broadcast for real-time customer messages
    const ch = supabase.channel(`ticket-${selectedId}`)
      .on('broadcast', { event: 'message' }, ({ payload }) => {
        // Only show customer messages here (agent messages shown optimistically)
        if (payload.sender_type !== 'user') return
        setMessages(p => {
          if (p.some(m => m.id === payload.id)) return p // deduplicate
          return [...p, payload]
        })
      })
      .subscribe()

    msgChannelRef.current = ch

    return () => {
      supabase.removeChannel(ch)
      msgChannelRef.current = null
    }
  }, [selectedId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (selectedId) setTimeout(() => inputRef.current?.focus(), 120)
  }, [selectedId])

  // ── Claim ticket when agent opens it ────────────────────
  async function openTicket(ticket) {
    setSelectedId(ticket.id)
    if (!ticket.assigned_to) {
      await supabase.from('support_tickets')
        .update({ assigned_to: user.email, updated_at: new Date().toISOString() })
        .eq('id', ticket.id)
        .is('assigned_to', null)
    }
  }

  // ── Send reply via Broadcast + persist to DB ─────────────
  async function sendReply() {
    const trimmed = input.trim()
    if (!trimmed || !selectedId || sending) return
    setSending(true)
    setInput('')

    const msgId = uuid()
    const newMsg = {
      id:          msgId,
      ticket_id:   selectedId,
      sender_type: 'agent',
      sender_name: agentDisplayName || user.email.split('@')[0],
      message:     trimmed,
      created_at:  new Date().toISOString(),
    }

    // Show immediately in agent UI
    setMessages(p => [...p, newMsg])

    // Persist to DB
    await supabase.from('support_messages').insert(newMsg)

    // Broadcast to customer
    msgChannelRef.current?.send({ type: 'broadcast', event: 'message', payload: newMsg })

    // Move ticket to pending once agent replies
    await supabase.from('support_tickets').update({
      updated_at:  new Date().toISOString(),
      assigned_to: user.email,
      status:      'pending',
    }).eq('id', selectedId).eq('status', 'open')

    setSending(false)
  }

  // ── Resolve ──────────────────────────────────────────────
  async function resolveTicket() {
    const msgId = uuid()
    const sysMsg = {
      id:          msgId,
      ticket_id:   selectedId,
      sender_type: 'system',
      sender_name: 'System',
      message:     '✅ This conversation has been resolved. Thank you for reaching out to Okoiya support! 💛',
      created_at:  new Date().toISOString(),
    }

    // Broadcast FIRST so customer sees it before we close our end
    msgChannelRef.current?.send({ type: 'broadcast', event: 'message', payload: sysMsg })

    await supabase.from('support_messages').insert(sysMsg)
    await supabase.from('support_tickets').update({
      status:     'resolved',
      closed_at:  new Date().toISOString(),
      close_note: resolveNote,
      updated_at: new Date().toISOString(),
    }).eq('id', selectedId)

    setShowResolve(false)
    setResolveNote('')
    setSelectedId(null)
  }

  // ── Access states ────────────────────────────────────────
  if (isAgent === null) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#FAFAF8' }}>
      <p style={{ fontFamily:'Nunito Sans,sans-serif', color:'#BCBCBC' }}>Checking access…</p>
    </div>
  )

  if (!isAgent) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#FAFAF8', flexDirection:'column', gap:'1rem' }}>
      <p style={{ fontFamily:'Gibs,serif', fontSize:'1.3rem', color:'#1A1A1A' }}>Access Restricted</p>
      <p style={{ fontFamily:'Nunito Sans,sans-serif', color:'#6B6B6B', fontSize:'0.875rem' }}>Your account isn't listed as a support agent.</p>
      <button onClick={() => navigate('/dashboard')} style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.85rem', color:'#C4826A', background:'none', border:'1px solid #C4826A', borderRadius:100, padding:'0.6rem 1.25rem', cursor:'pointer' }}>← Back to Dashboard</button>
    </div>
  )

  const filtered = tickets.filter(t => t.status === activeTab)
  const selected = tickets.find(t => t.id === selectedId)
  const counts = {
    open:     tickets.filter(t => t.status === 'open').length,
    pending:  tickets.filter(t => t.status === 'pending').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
  }

  const fmtTime = (d) => {
    if (!d) return ''
    const diff = Date.now() - new Date(d).getTime()
    if (diff < 60000)    return 'just now'
    if (diff < 3600000)  return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short' })
  }

  return (
    <div style={{ height:'100vh', display:'flex', flexDirection:'column', background:'#FAFAF8', overflow:'hidden' }}>

      {/* Header */}
      <div style={{ background:'white', borderBottom:'1px solid #E5E0D8', padding:'0 1.5rem', height:56, display:'flex', alignItems:'center', gap:'1rem', flexShrink:0 }}>
        <button onClick={() => navigate('/dashboard')} style={{ background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.4rem', fontFamily:'Nunito Sans,sans-serif', fontSize:'0.8rem', color:'#6B6B6B', padding:'0.4rem 0' }}>
          <ArrowLeft size={14}/> Dashboard
        </button>
        <div style={{ width:1, height:20, background:'#E5E0D8' }}/>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <MessageSquare size={16} color='#C4826A'/>
          <span style={{ fontFamily:'Gibs,serif', fontWeight:400, fontSize:'1.1rem', color:'#1A1A1A' }}>Support</span>
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background:'#4ADE80' }}/>
          <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.78rem', color:'#6B6B6B' }}>{user.email}</span>
        </div>
      </div>

      <style>{`
        .support-mobile-back{ display:none; }
        @media (max-width:760px){
          .support-list{ width:100% !important; border-right:none !important; }
          .support-pane{ width:100% !important; }
          .support-body.has-selection .support-list{ display:none !important; }
          .support-body:not(.has-selection) .support-pane{ display:none !important; }
          .support-mobile-back{ display:inline-flex !important; }
        }
      `}</style>

      <div className={`support-body${selectedId && selected ? ' has-selection' : ''}`} style={{ flex:1, display:'flex', overflow:'hidden' }}>

        {/* Left — ticket list */}
        <div className="support-list" style={{ width:280, borderRight:'1px solid #E5E0D8', display:'flex', flexDirection:'column', background:'white', flexShrink:0, overflow:'hidden' }}>
          <div style={{ display:'flex', borderBottom:'1px solid #E5E0D8', flexShrink:0 }}>
            {['open','pending','resolved'].map(t => (
              <button key={t} onClick={() => setActiveTab(t)} style={{ flex:1, padding:'0.75rem 0.25rem', background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans,sans-serif', fontSize:'0.72rem', fontWeight:activeTab===t?700:500, color:activeTab===t?'#C4826A':'#9B9B9B', borderBottom:activeTab===t?'2px solid #C4826A':'2px solid transparent', textTransform:'capitalize' }}>
                {t}
                {counts[t] > 0 && <span style={{ marginLeft:4, background:activeTab===t?'#C4826A':'#F3F0EA', color:activeTab===t?'white':'#6B6B6B', borderRadius:100, fontSize:'0.6rem', fontWeight:700, padding:'1px 5px' }}>{counts[t]}</span>}
              </button>
            ))}
          </div>

          <div style={{ flex:1, overflowY:'auto' }}>
            {filtered.length === 0 && (
              <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.8rem', color:'#BCBCBC', textAlign:'center', padding:'2rem 1rem' }}>No {activeTab} conversations</p>
            )}
            {filtered.map(t => (
              <button key={t.id} onClick={() => openTicket(t)}
                style={{ width:'100%', textAlign:'left', padding:'0.875rem 1rem', background:selectedId===t.id?'#FBF6F3':'white', border:'none', borderBottom:'1px solid #F5F0EA', cursor:'pointer', display:'flex', flexDirection:'column', gap:'0.2rem', transition:'background 0.1s', position:'relative' }}>
                {newIds.has(t.id) && <div style={{ position:'absolute', top:10, right:10, width:8, height:8, borderRadius:'50%', background:'#C4826A' }}/>}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.5rem' }}>
                  <span style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.couple_name || 'Customer'}</span>
                  <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.65rem', color:'#BCBCBC', flexShrink:0 }}>{fmtTime(t.updated_at)}</span>
                </div>
                {t.assigned_to && <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.68rem', color:'#9B9B9B' }}>→ {t.assigned_to.split('@')[0]}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Right — chat panel */}
        {selectedId && selected ? (
          <div className="support-pane" style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>

            {/* Chat header */}
            <div style={{ padding:'0.875rem 1.5rem', borderBottom:'1px solid #E5E0D8', display:'flex', alignItems:'center', gap:'0.75rem', flexShrink:0, background:'white' }}>
              <button onClick={() => setSelectedId(null)} className="support-mobile-back" aria-label="Back to conversations" style={{ background:'none', border:'none', cursor:'pointer', padding:'0.25rem 0.25rem 0.25rem 0', color:'#6B6B6B', alignItems:'center', flexShrink:0 }}>
                <ArrowLeft size={18}/>
              </button>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'0.95rem', color:'#1A1A1A', margin:0 }}>{selected.couple_name || 'Customer'}</p>
                <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.7rem', color:'#9B9B9B', margin:0 }}>
                  Opened {fmtTime(selected.created_at)}{selected.assigned_to ? ` · ${selected.assigned_to.split('@')[0]}` : ''}
                </p>
              </div>
              {selected.status !== 'resolved' && (
                <button onClick={() => setShowResolve(true)} style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'0.78rem', color:'white', background:'#9BAF9B', border:'none', borderRadius:100, padding:'0.55rem 1.125rem', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0 }}>
                  <CheckCircle size={13}/> Resolve
                </button>
              )}
              {selected.status === 'resolved' && (
                <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.72rem', color:'#9BAF9B', display:'flex', alignItems:'center', gap:'0.3rem' }}>
                  <CheckCircle size={12}/> Resolved {fmtTime(selected.closed_at)}
                </span>
              )}
              <button onClick={() => setSelectedId(null)} style={{ background:'none', border:'none', cursor:'pointer', padding:4, flexShrink:0 }}>
                <X size={16} color='#BCBCBC'/>
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex:1, overflowY:'auto', padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'0.875rem', background:'#FAFAF8' }}>
              {messages.map(m => {
                if (m.sender_type === 'system') return (
                  <div key={m.id} style={{ textAlign:'center' }}>
                    <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.72rem', color:'#9B9B9B', background:'white', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.3rem 0.875rem', whiteSpace:'pre-wrap' }}>{m.message}</span>
                  </div>
                )
                // DB rows use sender_type; broadcast payloads also use sender_type
                const isAgentMsg = m.sender_type === 'agent'
                return (
                  <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems:isAgentMsg?'flex-end':'flex-start' }}>
                    <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.65rem', color:'#BCBCBC', marginBottom:'0.2rem' }}>
                      {isAgentMsg ? (m.sender_name?.split('@')[0] || 'Agent') : (selected.couple_name || 'Customer')} · {fmtTime(m.created_at)}
                    </span>
                    <div style={{ maxWidth:'70%', padding:'0.75rem 1rem', borderRadius:isAgentMsg?'1rem 0 1rem 1rem':'0 1rem 1rem 1rem', background:isAgentMsg?'#C4826A':'white', border:isAgentMsg?'none':'1px solid #E5E0D8', fontFamily:'Nunito Sans,sans-serif', fontSize:'0.875rem', color:isAgentMsg?'white':'#1A1A1A', lineHeight:1.65, wordBreak:'break-word', whiteSpace:'pre-wrap' }}>
                      {m.message}
                    </div>
                  </div>
                )
              })}
              <div ref={bottomRef}/>
            </div>

            {/* Quick replies */}
            {selected.status !== 'resolved' && (
              <div style={{ padding:'0.5rem 1.25rem 0', borderTop:'1px solid #E5E0D8', display:'flex', gap:'0.375rem', flexWrap:'wrap', flexShrink:0, background:'white' }}>
                {QUICK_REPLIES.map((r, i) => (
                  <button key={i} onClick={() => { setInput(r); setTimeout(() => inputRef.current?.focus(), 50) }}
                    style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.72rem', color:'#C4826A', background:'white', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.3rem 0.75rem', cursor:'pointer', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:200, transition:'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor='#C4826A'}
                    onMouseLeave={e => e.currentTarget.style.borderColor='#E5E0D8'}>
                    {r.length > 38 ? r.slice(0, 38) + '…' : r}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            {selected.status !== 'resolved' ? (
              <div style={{ padding:'0.75rem 1.25rem', borderTop:'1px solid #E5E0D8', display:'flex', gap:'0.5rem', flexShrink:0, background:'white' }}>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendReply() } }}
                  placeholder="Reply to customer…"
                  style={{ flex:1, fontFamily:'Nunito Sans,sans-serif', fontSize:'0.875rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.6rem 1rem', outline:'none' }}
                  onFocus={e => e.target.style.borderColor='#C4826A'}
                  onBlur={e  => e.target.style.borderColor='#E5E0D8'}
                />
                <button onClick={sendReply} disabled={!input.trim()||sending}
                  style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'0.78rem', color:'white', background:!input.trim()||sending?'#D4C4BC':'#C4826A', border:'none', borderRadius:100, padding:'0.6rem 1.25rem', cursor:!input.trim()||sending?'default':'pointer', transition:'background 0.15s', flexShrink:0 }}>
                  SEND
                </button>
              </div>
            ) : (
              <div style={{ padding:'0.75rem 1.25rem', background:'#F9F6F2', borderTop:'1px solid #E5E0D8', flexShrink:0 }}>
                {selected.close_note
                  ? <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:0, display:'flex', gap:'0.4rem', alignItems:'flex-start' }}><Clock size={12} style={{ flexShrink:0, marginTop:2 }}/> {selected.close_note}</p>
                  : <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.78rem', color:'#BCBCBC', margin:0, textAlign:'center' }}>Conversation resolved</p>
                }
              </div>
            )}
          </div>
        ) : (
          <div className="support-pane" style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'0.75rem', background:'#FAFAF8' }}>
            <MessageSquare size={36} color='#E5E0D8'/>
            <p style={{ fontFamily:'Nunito Sans,sans-serif', color:'#BCBCBC', fontSize:'0.875rem' }}>Select a conversation to start</p>
          </div>
        )}
      </div>

      {/* Resolve modal */}
      {showResolve && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:'1.25rem', padding:'1.75rem', maxWidth:380, width:'90%', boxShadow:'0 16px 48px rgba(0,0,0,0.18)', animation:'resolveIn 0.2s ease-out both' }}>
            <style>{`@keyframes resolveIn{from{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}`}</style>
            <h3 style={{ fontFamily:'Gibs,serif', fontWeight:400, fontSize:'1.1rem', color:'#1A1A1A', margin:'0 0 0.5rem' }}>Resolve conversation</h3>
            <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.82rem', color:'#6B6B6B', margin:'0 0 1rem' }}>Add an optional note for your records.</p>
            <textarea
              value={resolveNote}
              onChange={e => setResolveNote(e.target.value)}
              placeholder="e.g. Helped couple re-add guests after import issue"
              rows={3}
              style={{ width:'100%', fontFamily:'Nunito Sans,sans-serif', fontSize:'0.85rem', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.75rem', outline:'none', resize:'none', boxSizing:'border-box' }}
              onFocus={e => e.target.style.borderColor='#C4826A'}
              onBlur={e  => e.target.style.borderColor='#E5E0D8'}
            />
            <div style={{ display:'flex', gap:'0.5rem', marginTop:'1rem' }}>
              <button onClick={() => setShowResolve(false)} style={{ flex:1, fontFamily:'Nunito Sans,sans-serif', fontSize:'0.85rem', color:'#6B6B6B', background:'#F5F0EA', border:'none', borderRadius:100, padding:'0.75rem', cursor:'pointer' }}>Cancel</button>
              <button onClick={resolveTicket} style={{ flex:1, fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'0.85rem', color:'white', background:'#9BAF9B', border:'none', borderRadius:100, padding:'0.75rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem' }}>
                <CheckCircle size={14}/> Mark resolved
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
